import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Form, Input, Button, Select, DatePicker, Checkbox, Alert, Table, Typography, Tag, Space, message } from 'antd';
import { CheckCircleOutlined, ExclamationCircleOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { CustomerQuote, ShippingDestination } from '../types.ts';
import { customerQuoteAPI, shippingDestinationAPI } from '../services/api.ts';
import { formatCurrency } from '../utils/numberFormatting.ts';
import { formatProductDisplay } from '../utils/productUtils.ts';

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;

// Conversione preventivo cliente → ordine cliente. Stesso schema a passi del modal
// "Trasforma in Ordine Fornitore": conferma (dati precompilati dal preventivo, tutti
// modificabili tranne lo stato, fisso "Nuovo") → elaborazione → risultato.
interface ConvertToOrderModalProps {
  visible: boolean;
  quote: CustomerQuote | null;
  onClose: () => void;
  onConverted?: () => void;
}

const ConvertToOrderModal: React.FC<ConvertToOrderModalProps> = ({ visible, quote, onClose, onConverted }) => {
  const [form] = Form.useForm();
  const [step, setStep] = useState<'confirm' | 'processing' | 'result'>('confirm');
  const [result, setResult] = useState<any>(null);
  const [shippingDestinations, setShippingDestinations] = useState<ShippingDestination[]>([]);

  const loadShippingDestinations = useCallback(async (customerId: number) => {
    try {
      const data = await shippingDestinationAPI.getShippingDestinations(undefined, customerId);
      setShippingDestinations(data || []);
    } catch (error) {
      setShippingDestinations([]);
    }
  }, []);

  useEffect(() => {
    if (!quote || !visible) return;
    setStep('confirm');
    setResult(null);
    loadShippingDestinations(quote.customer_id);
    form.setFieldsValue({
      order_number: `ORD-${quote.quote_number}`.toUpperCase(),
      order_date: dayjs(),
      requested_delivery_date: quote.requested_delivery_date ? dayjs(quote.requested_delivery_date) : dayjs().add(15, 'day'),
      customer_order_number: quote.customer_reference || undefined,
      shipping_destination_id: quote.shipping_destination_id || undefined,
      reorder: !!quote.reorder,
      notes: quote.notes || undefined,
    });
  }, [quote, visible, form, loadShippingDestinations]);

  const handleConvert = async () => {
    if (!quote) return;
    let values: any;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setStep('processing');
    try {
      const payload = {
        order_number: values.order_number,
        order_date: values.order_date ? values.order_date.format('YYYY-MM-DD') : undefined,
        requested_delivery_date: values.requested_delivery_date ? values.requested_delivery_date.format('YYYY-MM-DD') : null,
        customer_order_number: values.customer_order_number || null,
        shipping_destination_id: values.shipping_destination_id || null,
        reorder: !!values.reorder,
        notes: values.notes || null,
      };
      const response = await customerQuoteAPI.convertToOrder(quote.id, payload);
      setResult(response);
      setStep('result');
      message.success(response?.message || 'Preventivo convertito in ordine');
      onConverted?.();
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      message.error(typeof detail === 'string' ? detail : 'Errore nella conversione del preventivo');
      setStep('confirm');
    }
  };

  const handleClose = () => {
    setStep('confirm');
    setResult(null);
    onClose();
  };

  if (!quote) return null;
  const details = quote.quote_details || [];

  const rowColumns = [
    { title: 'Prodotto', key: 'product', ellipsis: true,
      render: (_: any, r: any) => r.product ? <Text code title={formatProductDisplay(r.product)}>{formatProductDisplay(r.product)}</Text> : `ID ${r.product_id}` },
    { title: 'Qta', dataIndex: 'quantity', key: 'quantity', width: 70, align: 'center' as const },
    { title: 'Vendita Unit.', dataIndex: 'unit_price', key: 'unit_price', width: 110, align: 'right' as const,
      render: (v: number) => `€ ${formatCurrency(v || 0)}` },
    { title: 'Totale', dataIndex: 'total_price', key: 'total_price', width: 110, align: 'right' as const,
      render: (v: number) => `€ ${formatCurrency(v || 0)}` },
  ];

  const renderConfirm = () => (
    <div>
      <Alert
        message={`Conversione del preventivo ${quote.quote_number}`}
        description={
          <span>
            Cliente: <strong>{quote.customer?.company || 'N/A'}</strong> — Importo: <strong>€ {formatCurrency(quote.total_amount || 0)}</strong>.
            L'ordine nasce in stato <Tag color="blue">Nuovo</Tag> con le righe del preventivo; il preventivo passa a <Tag color="purple">Evaso</Tag>.
          </span>
        }
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Form form={form} layout="vertical">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Form.Item name="order_number" label="Numero Ordine" rules={[{ required: true, message: 'Inserire il numero ordine' }, { max: 50, message: 'Massimo 50 caratteri' }]}>
            <Input
              placeholder="Numero Ordine"
              style={{ textTransform: 'uppercase' }}
              onChange={(e) => form.setFieldValue('order_number', e.target.value.toUpperCase())}
            />
          </Form.Item>
          <Form.Item name="customer_order_number" label="Numero Ordine Cliente (dal riferimento del preventivo)" rules={[{ max: 50, message: 'Massimo 50 caratteri' }]}>
            <Input
              placeholder="Numero ordine cliente (opzionale)"
              style={{ textTransform: 'uppercase' }}
              onChange={(e) => form.setFieldValue('customer_order_number', e.target.value.toUpperCase())}
            />
          </Form.Item>
          <Form.Item name="order_date" label="Data Ordine" rules={[{ required: true, message: 'Selezionare la data ordine' }]}>
            <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="requested_delivery_date" label="Data Consegna Richiesta">
            <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="Stato Ordine" tooltip="L'ordine generato nasce sempre in stato 'Nuovo': gli stati successivi si impostano da Ordini Cliente → Cambia Stato.">
            <Input value="Nuovo" disabled />
          </Form.Item>
          <Form.Item name="shipping_destination_id" label="Destinazione Spedizione">
            <Select placeholder="Nessuna destinazione" allowClear>
              {shippingDestinations.map((destination) => (
                <Option key={destination.id} value={destination.id}>
                  {destination.name}{destination.address ? ` - ${destination.address}` : ''}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="reorder" valuePropName="checked" style={{ gridColumn: 'span 2' }}>
            <Checkbox>
              <strong>Ordine Stock</strong> - Forza creazione ordini fornitori anche se i prodotti sono disponibili in stock
            </Checkbox>
          </Form.Item>
        </div>
        <Form.Item name="notes" label="Note Ordine">
          <TextArea rows={2} placeholder="Note per l'ordine (precompilate dal preventivo)" />
        </Form.Item>
      </Form>

      <Title level={5}>Righe che verranno riportate nell'ordine</Title>
      <Table
        columns={rowColumns}
        dataSource={details}
        rowKey={(r: any) => r.id ?? r.product_id}
        size="small"
        pagination={false}
        summary={() => (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={3}><Text strong>Totale</Text></Table.Summary.Cell>
            <Table.Summary.Cell index={1} align="right">
              <Text strong>€ {formatCurrency(details.reduce((s, d) => s + (d.total_price || 0), 0))}</Text>
            </Table.Summary.Cell>
          </Table.Summary.Row>
        )}
      />
    </div>
  );

  const renderProcessing = () => (
    <div style={{ textAlign: 'center', padding: '40px 0' }}>
      <ShoppingCartOutlined style={{ fontSize: 48, color: '#1890ff', marginBottom: 16 }} />
      <Title level={4}>Conversione in corso...</Title>
      <Text>Sto creando l'ordine cliente dal preventivo</Text>
    </div>
  );

  const renderResult = () => (
    <div style={{ textAlign: 'center' }}>
      <CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a', marginBottom: 16 }} />
      <Title level={4}>Conversione Completata</Title>
      <Text>{result?.message}</Text>
      {result?.order && (
        <div style={{ marginTop: 16 }}>
          <Space direction="vertical">
            <Text>Ordine creato: <Tag color="green" style={{ fontSize: 14 }}>{result.order.order_number}</Tag></Text>
            <Text>Stato: <Tag color="blue">{result.order.order_status || 'Nuovo'}</Tag></Text>
            {result.order.total_amount != null && <Text>Importo: <strong>€ {formatCurrency(result.order.total_amount)}</strong></Text>}
            <Text type="secondary">L'ordine è disponibile in Ordini → Ordini Cliente.</Text>
          </Space>
        </div>
      )}
    </div>
  );

  return (
    <Modal
      title={<Space><ExclamationCircleOutlined style={{ color: '#faad14' }} />Converti Preventivo in Ordine</Space>}
      open={visible}
      onCancel={handleClose}
      width={step === 'confirm' ? 900 : 600}
      destroyOnClose
      footer={
        step === 'confirm' ? [
          <Button key="cancel" onClick={handleClose}>Annulla</Button>,
          <Button key="convert" type="primary" onClick={handleConvert}>Converti in Ordine</Button>,
        ] : step === 'processing' ? null : [
          <Button key="close" type="primary" onClick={handleClose}>Chiudi</Button>,
        ]
      }
    >
      {step === 'confirm' && renderConfirm()}
      {step === 'processing' && renderProcessing()}
      {step === 'result' && renderResult()}
    </Modal>
  );
};

export default ConvertToOrderModal;
